/**
 * Obsidian Amber Light Theme
 *
 * Warm light surfaces with amber primary actions.
 */
// Text tones the sidebar shares with the main ladder, declared once so raising
// one raises both. These were copied hex values, and a contrast fix to the
// ladder left the sidebar behind without anything failing.
const TEXT_HEADING = '#2A1C00';

const obsidianAmberLightTheme = {
  name: 'obsidian-amber-light',
  label: 'Obsidian Amber Light',
  mode: 'light',
  colorSet: 'obsidian-amber',

  colors: {
    // Background elevation system
    canvas: '#FEF6E8',
    panelPrimary: '#FFFFFF',
    panelSecondary: '#FEF0D6',
    panelTertiary: '#FDE8C0',
    panelQuaternary: '#FDDFA8',
    surfaceHover: '#FDDFA8',

    // Borders
    borderSubtle: '#FDD68E',
    borderStrong: '#D97706',

    // Text hierarchy
    textHeading: TEXT_HEADING,
    textPrimary: '#3B2A00',
    textSecondary: '#6F4F1F',
    textSupporting: '#7A5A24',
    textNeutral: '#82632D',
    textMuted: '#82632D',
    textInverse: '#FFFFFF',

    // Primary accent: amber
    accentPrimary: '#B45309',
    accentPrimaryVibrant: '#92400E',

    // Secondary accent: green
    accentSecondary: '#166534',
    accentSecondaryVibrant: '#14532D',

    // Warm accent: CTA and warning amber
    accentWarm: '#B45309',
    accentWarmVibrant: '#92400E',

    // Status colors
    statusDanger: '#991B1B',
    statusWarning: '#854D0E',
    statusSuccess: '#166534',
    statusInfo: '#92400E',

    // Support colors
    gridline: 'rgba(59, 42, 0, 0.10)',
    focusOutline: 'rgba(180, 83, 9, 0.42)',
    overlay: 'rgba(59, 42, 0, 0.40)',

    // Component-specific
    headerBg: '#FFFFFF',
    sidebarBg: '#FEF0D6',
    cardBg: '#FFFFFF',
    inputBg: '#FFFFFF',
    dropdownBg: '#FFFFFF',

    // Navigation sidebar
    navSectionHeader: '#87672F',
    navItemText: '#5A3E11',
    navItemHover: TEXT_HEADING,
    navItemHoverBg: 'rgba(180, 83, 9, 0.08)',
    navItemActive: '#92400E',
    navItemActiveBg: 'rgba(180, 83, 9, 0.13)',
    navIconDefault: '#87672F',
    navIconHover: '#92400E',
    navIconActive: '#92400E',
    navDivider: 'rgba(139, 106, 48, 0.28)',

    // Table cell colors
    tableEntity: '#3B2A00',
    tableMetric: '#166534',
    tableBody: '#4A3308',
    tableSecondary: '#6F4F1F',
    tableTime: '#7A5A24',

    // Quality distribution colors
    qualityP20: '#991B1B',
    qualityP40: '#C2410C',
    qualityP60: '#854D0E',
    qualityP80: '#15803D',
    qualityP100: '#166534',

    // Pill styling
    pillBgFrom: '#FEF0D6',
    pillBgTo: '#FDE8C0',
    pillActiveBgFrom: '#FDE8C0',
    pillActiveBgTo: '#FDDFA8',
    pillHoverBgFrom: '#FDE8C0',
    pillHoverBgTo: '#FDDFA8',

    // Table-specific
    tableHeaderBg: '#FDE8C0',
    tableRowHover: 'rgba(180, 83, 9, 0.07)',
    tableRowExpanded: 'rgba(22, 101, 52, 0.07)',
    buttonTextOnAccent: '#FFFFFF',

    // RGB channels for opacity modifiers
    statusDangerRgb: '153, 27, 27',
    statusSuccessRgb: '22, 101, 52',
    statusWarningRgb: '133, 77, 14',
    statusInfoRgb: '146, 64, 14',
    accentWarmRgb: '180, 83, 9',

    // Severity
    severityCriticalText: '#991B1B',
    severityCriticalBg: 'rgba(153, 27, 27, 0.10)',
    severityCriticalBorder: 'rgba(153, 27, 27, 0.24)',
    severityCriticalGlow: '0 0 18px rgba(153, 27, 27, 0.14)',
    severityHighText: '#B45309',
    severityHighBg: 'rgba(180, 83, 9, 0.10)',
    severityHighBorder: 'rgba(180, 83, 9, 0.24)',
    severityHighGlow: '0 0 18px rgba(180, 83, 9, 0.14)',
    severityMediumText: '#854D0E',
    severityMediumBg: 'rgba(133, 77, 14, 0.10)',
    severityMediumBorder: 'rgba(133, 77, 14, 0.24)',
    severityMediumGlow: '0 0 18px rgba(133, 77, 14, 0.14)',
    severityLowText: '#92400E',
    severityLowBg: 'rgba(146, 64, 14, 0.10)',
    severityLowBorder: 'rgba(146, 64, 14, 0.22)',
    severityLowGlow: '0 0 18px rgba(146, 64, 14, 0.14)',
    severitySuccessText: '#166534',
    severitySuccessBg: 'rgba(22, 101, 52, 0.10)',
    severitySuccessBorder: 'rgba(22, 101, 52, 0.22)',
    severitySuccessGlow: '0 0 8px rgba(22, 101, 52, 0.14)',

    // Border hierarchy
    borderMuted: '#FDDFA8',
    borderMedium: '#FDD68E',
    borderAccent: '#D97706',

    // Surface overlays
    surfaceSubtle: 'rgba(59, 42, 0, 0.035)',
    surfaceMid: 'rgba(59, 42, 0, 0.065)',
    surfaceOverlayHover: 'rgba(180, 83, 9, 0.065)',

    // Scrollbar
    scrollbarTrack: 'rgba(253, 230, 138, 0.55)',
    scrollbarThumb: 'rgba(139, 106, 48, 0.55)',
    scrollbarThumbHover: 'rgba(111, 79, 31, 0.72)',

    // Selection
    selectionBg: 'rgba(180, 83, 9, 0.20)',

    // Graph visualization
    graphBg: '#FEF6E8',
    graphNodeBg: '#FFFFFF',
    graphNodeText: '#3B2A00',
  },

  shadows: {
    card: '0 1px 2px rgba(59, 42, 0, 0.06), 0 8px 20px rgba(59, 42, 0, 0.07)',
    soft: '0 2px 8px rgba(59, 42, 0, 0.06), 0 16px 32px rgba(59, 42, 0, 0.08)',
    dropdown: '0 8px 24px rgba(59, 42, 0, 0.14), 0 1px 3px rgba(59, 42, 0, 0.08)',
    drawer: '-8px 0 24px rgba(59, 42, 0, 0.10), -1px 0 3px rgba(59, 42, 0, 0.07)',
  },
};

export default obsidianAmberLightTheme;
